import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const CONTACT_URL = 'https://readdy.ai/api/form/da57vbb0muk3qhp2tsg0';
const VOLUNTEER_URL = 'https://readdy.ai/api/form/da57vbb0muk3qhp2tsgg';

type Status = { type: 'idle' | 'sending' | 'success' | 'error'; message: string };

const inputClass =
  'w-full rounded-md border border-background-300 bg-white px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400';
const labelClass = 'block text-sm font-medium text-foreground-800 mb-1.5';

async function submitForm(
  event: FormEvent<HTMLFormElement>,
  setStatus: (s: Status) => void,
  successMsg: string,
  errorMsg: string,
) {
  event.preventDefault();
  const form = event.currentTarget;
  const formData = new FormData(form);

  const honeypot = ((formData.get('website_alt') as string) || '').trim();
  if (honeypot) {
    setStatus({ type: 'success', message: successMsg });
    form.reset();
    return;
  }

  const payload = new URLSearchParams();
  formData.forEach((value, key) => {
    if (key !== 'website_alt') {
      payload.append(key, String(value));
    }
  });

  setStatus({ type: 'sending', message: '' });
  try {
    const res = await fetch(form.action, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: payload.toString(),
    });
    const responseText = await res.text();
    let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
    try {
      parsed = JSON.parse(responseText);
    } catch {
      parsed = null;
    }

    const serverMsg =
      parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

    if (res.ok && parsed?.code === 'OK') {
      setStatus({ type: 'success', message: successMsg });
      form.reset();
    } else {
      setStatus({ type: 'error', message: serverMsg || errorMsg });
    }
  } catch {
    setStatus({ type: 'error', message: errorMsg });
  }
}

function StatusMessage({ status }: { status: Status }) {
  if (status.type === 'idle' || status.type === 'sending') return null;
  const isSuccess = status.type === 'success';
  return (
    <p
      className={`mt-3 text-sm font-medium ${
        isSuccess ? 'text-primary-700' : 'text-red-600'
      }`}
    >
      {status.message}
    </p>
  );
}

export default function Contact() {
  const { t } = useTranslation();
  const [contactStatus, setContactStatus] = useState<Status>({ type: 'idle', message: '' });
  const [volunteerStatus, setVolunteerStatus] = useState<Status>({ type: 'idle', message: '' });

  return (
    <section id="contact" className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-semibold uppercase tracking-wide">
            {t('contact.tag')}
          </span>
          <h2 className="mt-4 font-heading font-bold text-2xl md:text-4xl text-foreground-950">
            {t('contact.title')}
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-sm md:text-base text-foreground-600">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Contact info */}
          <div className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-white p-6 md:p-7">
              <h3 className="font-heading font-bold text-lg text-foreground-950 mb-5">
                {t('footer.contact')}
              </h3>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <i className="ri-map-pin-2-line"></i>
                  </span>
                  <div>
                    <div className="text-xs text-foreground-500">{t('contact.addressLabel')}</div>
                    <div className="text-foreground-800 font-medium">{t('contact.address')}</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                    <i className="ri-phone-line"></i>
                  </span>
                  <div>
                    <div className="text-xs text-foreground-500">{t('contact.phoneLabel')}</div>
                    <div className="text-foreground-800 font-medium">{t('contact.phone')}</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className="ri-mail-line"></i>
                  </span>
                  <div>
                    <div className="text-xs text-foreground-500">{t('contact.emailLabel')}</div>
                    <div className="text-foreground-800 font-medium">{t('contact.email')}</div>
                  </div>
                </li>
              </ul>

              <div className="mt-6 rounded-md overflow-hidden border border-background-200">
                <iframe
                  title="Insani Darul Hana location map"
                  src="https://maps.google.com/maps?q=18%20Lorong%20Astana%203%2C%20Jalan%20Astana%2C%20Petra%20Jaya%2C%2093050%20Kuching%2C%20Sarawak&t=&z=16&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-52"
                  loading="lazy"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Contact form */}
          <div className="rounded-lg border border-background-200 bg-white p-6 md:p-7">
            <h3 className="font-heading font-bold text-lg text-foreground-950">
              {t('contact.formTitle')}
            </h3>
            <p className="text-sm text-foreground-500 mt-1">{t('contact.formSubtitle')}</p>

            <form
              data-readdy-form
              action={CONTACT_URL}
              method="post"
              className="mt-5 space-y-4"
              onSubmit={(e) =>
                submitForm(e, setContactStatus, t('contact.success'), t('contact.error'))
              }
            >
              <div>
                <label htmlFor="contact-name" className={labelClass}>
                  {t('contact.name')}
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  placeholder={t('contact.namePlaceholder')}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className={labelClass}>
                  {t('contact.emailField')}
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder={t('contact.emailPlaceholder')}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-phone" className={labelClass}>
                  {t('contact.phoneField')}
                </label>
                <input
                  id="contact-phone"
                  name="phone"
                  type="tel"
                  placeholder={t('contact.phonePlaceholder')}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-subject" className={labelClass}>
                  {t('contact.subject')}
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  placeholder={t('contact.subjectPlaceholder')}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-message" className={labelClass}>
                  {t('contact.message')}
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={4}
                  required
                  maxLength={500}
                  placeholder={t('contact.messagePlaceholder')}
                  className={inputClass}
                ></textarea>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="contact-extras"
              />

              <button
                type="submit"
                disabled={contactStatus.type === 'sending'}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 font-semibold text-sm whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer"
              >
                {contactStatus.type === 'sending' ? t('contact.sending') : t('contact.submit')}
              </button>
              <StatusMessage status={contactStatus} />
            </form>
          </div>
        </div>

        {/* Volunteer form */}
        <div className="mt-6 lg:mt-8 rounded-lg border border-background-200 bg-white p-6 md:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
            <div>
              <h3 className="font-heading font-bold text-lg md:text-xl text-foreground-950">
                {t('contact.volunteerTitle')}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                {t('contact.volunteerSubtitle')}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  <i className="ri-time-line"></i> Flexible
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                  <i className="ri-team-line"></i> Community
                </span>
              </div>
            </div>

            <form
              data-readdy-form
              action={VOLUNTEER_URL}
              method="post"
              className="space-y-4"
              onSubmit={(e) =>
                submitForm(e, setVolunteerStatus, t('contact.success'), t('contact.error'))
              }
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="vol-name" className={labelClass}>
                    {t('contact.name')}
                  </label>
                  <input
                    id="vol-name"
                    name="name"
                    type="text"
                    required
                    placeholder={t('contact.namePlaceholder')}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="vol-email" className={labelClass}>
                    {t('contact.emailField')}
                  </label>
                  <input
                    id="vol-email"
                    name="email"
                    type="email"
                    required
                    placeholder={t('contact.emailPlaceholder')}
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="vol-phone" className={labelClass}>
                    {t('contact.phoneField')}
                  </label>
                  <input
                    id="vol-phone"
                    name="phone"
                    type="tel"
                    placeholder={t('contact.phonePlaceholder')}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="vol-area" className={labelClass}>
                    {t('contact.areaLabel')}
                  </label>
                  <select id="vol-area" name="area" className={inputClass}>
                    <option value={t('contact.area1')}>{t('contact.area1')}</option>
                    <option value={t('contact.area2')}>{t('contact.area2')}</option>
                    <option value={t('contact.area3')}>{t('contact.area3')}</option>
                    <option value={t('contact.area4')}>{t('contact.area4')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="vol-availability" className={labelClass}>
                  {t('contact.availabilityLabel')}
                </label>
                <input
                  id="vol-availability"
                  name="availability"
                  type="text"
                  placeholder={t('contact.availabilityPlaceholder')}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="vol-message" className={labelClass}>
                  {t('contact.volunteerMessage')}
                </label>
                <textarea
                  id="vol-message"
                  name="message"
                  rows={3}
                  maxLength={500}
                  placeholder={t('contact.volunteerMessagePlaceholder')}
                  className={inputClass}
                ></textarea>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="contact-extras"
              />

              <button
                type="submit"
                disabled={volunteerStatus.type === 'sending'}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 font-semibold text-sm whitespace-nowrap hover:bg-accent-600 transition-colors disabled:opacity-60 cursor-pointer"
              >
                <i className="ri-team-line"></i>
                {volunteerStatus.type === 'sending'
                  ? t('contact.sending')
                  : t('contact.volunteerSubmit')}
              </button>
              <StatusMessage status={volunteerStatus} />
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}